'use client';

import { useMemo } from 'react';

import { Tooltip } from '@/shared/components/tooltip';

import { cn } from '@/lib/utils';

import { getRandomAppColorByText } from '../utils/color';

type StageData = {
	stage: string;
	count: number;
};

type StageDistributionProgressBarProps = {
	/** Array of stages with their counts */
	stages: StageData[];
	/** Height of the progress bar */
	height?: 'sm' | 'md' | 'lg';
	/** Additional class name */
	className?: string;
	/** Whether to show legend below the bar */
	showLegend?: boolean;
};

const HEIGHT_CLASSES = {
	sm: 'h-2',
	md: 'h-3',
	lg: 'h-4',
};

export function StageDistributionProgressBar({
	stages,
	height = 'md',
	className,
	showLegend = false,
}: StageDistributionProgressBarProps) {
	const total = useMemo(() => stages.reduce((sum, stage) => sum + stage.count, 0), [stages]);

	const stagesWithColors = useMemo(
		() =>
			stages.map((stage) => ({
				...stage,
				color: getRandomAppColorByText(stage?.stage ?? '', true),
				percentage: total > 0 ? (stage.count / total) * 100 : 0,
			})),
		[stages, total]
	);

	const tooltipContent = useMemo(
		() => (
			<div className="flex flex-col gap-1 text-xs">
				{stagesWithColors
					.filter((stage) => stage.percentage > 0)
					.map((stage) => (
						<div
							key={stage.stage}
							className="flex items-center justify-between gap-4"
						>
							<div className="flex items-center gap-1.5">
								<div
									className="h-2 w-2 rounded-full"
									style={{ backgroundColor: stage.color }}
								/>
								<span>{stage.stage}</span>
							</div>
							<span className="font-medium">{stage.percentage.toFixed(1)}%</span>
						</div>
					))}
			</div>
		),
		[stagesWithColors]
	);

	if (total === 0) {
		return (
			<div className={cn('w-full rounded-full bg-muted', HEIGHT_CLASSES[height], className)} />
		);
	}

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{/* Progress Bar with single tooltip */}
			<Tooltip content={tooltipContent}>
				<div
					className={cn(
						'flex w-full cursor-pointer overflow-hidden rounded-full',
						HEIGHT_CLASSES[height]
					)}
				>
					{stagesWithColors.map((stage) => {
						if (stage.percentage === 0) return null;

						return (
							<div
								key={stage.stage}
								className="transition-opacity"
								style={{
									width: `${stage.percentage}%`,
									backgroundColor: stage.color,
								}}
							/>
						);
					})}
				</div>
			</Tooltip>

			{/* Legend */}
			{showLegend && (
				<div className="flex flex-wrap gap-x-4 gap-y-1">
					{stagesWithColors
						.filter((stage) => stage.percentage > 0)
						.map((stage) => (
							<div
								key={stage.stage}
								className="flex items-center gap-1.5 text-xs"
							>
								<div
									className="h-2 w-2 rounded-full"
									style={{ backgroundColor: stage.color }}
								/>
								<span className="text-muted-foreground">
									{stage.stage}: {stage.count.toLocaleString()}
								</span>
							</div>
						))}
				</div>
			)}
		</div>
	);
}

export default StageDistributionProgressBar;
